/**
 * Per-user theme preference (dark / light / system) persisted in localStorage.
 * Applies the matching class on `<html>` so the CSS variables in index.css
 * (`:root.light` / `:root.dark`) flip the entire palette.
 *
 * Backed by a single module-level store (not per-component useState) so EVERY
 * consumer — the Topbar toggle, the Brand logo (which swaps light/dark logos),
 * every shell — shares one source of truth and re-renders together. Without
 * this, toggling in one component left others (e.g. the logo) stale.
 */
import { useCallback, useSyncExternalStore } from "react";

export type ThemeMode = "dark" | "light" | "system";

const STORAGE_KEY = "rdfx:theme";

function systemPref(): "dark" | "light" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "dark" || v === "light" || v === "system") return v;
  return "dark"; // app default is the premium dark theme
}

function applyClass(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const resolved = mode === "system" ? systemPref() : mode;
  root.classList.remove("dark", "light");
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
}

/* ─── shared store ─────────────────────────────────────────────────────── */
let currentMode: ThemeMode = readStored();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function setModeStore(next: ThemeMode) {
  currentMode = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // localStorage can throw in private mode — harmless to ignore.
  }
  applyClass(next);
  emit();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): ThemeMode {
  return currentMode;
}

// Apply the stored theme immediately on load, and keep "system" in sync
// with the OS. Guarded for SSR (Vite is CSR, but stay safe).
if (typeof window !== "undefined") {
  applyClass(currentMode);
  window.matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
    if (currentMode === "system") {
      applyClass("system");
      emit();
    }
  });
  // Sync across browser tabs.
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      const next = readStored();
      if (next !== currentMode) {
        currentMode = next;
        applyClass(next);
        emit();
      }
    }
  });
}

export function useTheme() {
  const mode = useSyncExternalStore(subscribe, getSnapshot, () => "dark" as ThemeMode);

  const setMode = useCallback((m: ThemeMode) => setModeStore(m), []);

  const toggle = useCallback(() => {
    const resolved = currentMode === "system" ? systemPref() : currentMode;
    setModeStore(resolved === "dark" ? "light" : "dark");
  }, []);

  const resolved: "dark" | "light" = mode === "system" ? systemPref() : mode;

  return { mode, resolved, setMode, toggle };
}
